import React, { useState, useEffect } from 'react';
import { Button } from '@headlessui/react';
import { FaEdit, FaTimes, FaRocket, FaCrown, FaStar, FaExclamationCircle, FaSignOutAlt, FaCreditCard, FaCog } from 'react-icons/fa';
import { 
  getToken, 
  getUser, 
  isTokenValid, 
  redirectToLogin,
  authenticatedFetch,
  getUserLimits
} from '../utils/tokenUtils';

const Settings = ({ user, onLogout, onUserUpdate, onUserChange, onShowLogoutDialog }) => {
  const [showSettingsForm, setShowSettingsForm] = useState(false);
  
  // Get cached user limits for plan display
  const limits = getUserLimits();
  const userPlan = limits?.plan || 'free';
  const [settingsData, setSettingsData] = useState({
    address: '',
    phone: '',
    company: '',
    website: '',
    timezone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);

  // Common timezones
  const timezones = [
    { value: 'Europe/Istanbul', label: 'Europe/Istanbul (GMT+3)' },
    { value: 'Europe/London', label: 'Europe/London (GMT+0/+1)' },
    { value: 'Europe/Paris', label: 'Europe/Paris (GMT+1/+2)' },
    { value: 'Europe/Berlin', label: 'Europe/Berlin (GMT+1/+2)' },
    { value: 'Europe/Moscow', label: 'Europe/Moscow (GMT+3)' },
    { value: 'America/New_York', label: 'America/New_York (GMT-5/-4)' },
    { value: 'America/Los_Angeles', label: 'America/Los_Angeles (GMT-8/-7)' },
    { value: 'America/Chicago', label: 'America/Chicago (GMT-6/-5)' },
    { value: 'Asia/Tokyo', label: 'Asia/Tokyo (GMT+9)' },
    { value: 'Asia/Shanghai', label: 'Asia/Shanghai (GMT+8)' },
    { value: 'Asia/Dubai', label: 'Asia/Dubai (GMT+4)' },
    { value: 'Australia/Sydney', label: 'Australia/Sydney (GMT+10/+11)' },
    { value: 'UTC', label: 'UTC (GMT+0)' }
  ];

  // Initialize settings data when user data is available
  useEffect(() => {
    console.log('Settings useEffect - User prop changed:', user);
    console.log('Settings useEffect - User profile:', user?.profile);
    
    if (user?.profile) {
      setSettingsData({
        address: user.profile.address || '',
        phone: user.profile.phone || '',
        company: user.profile.company || '',
        website: user.profile.website || '',
        timezone: user.profile.timezone || 'Europe/Istanbul'
      });
    }
  }, [user?.profile]);

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  // Fetch user data
  const fetchUserData = async () => {
    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }
      
      const response = await authenticatedFetch('/api/auth/me');
      
      if (response.ok) {
        const userData = await response.json();
        console.log('Settings fetchUserData - API response:', userData);
        console.log('Settings fetchUserData - User profile:', userData.user?.profile);
        
        // Update user data in parent component
        if (onUserChange && userData.user) {
          onUserChange(userData.user);
        }
        
        // Update settings data with fresh data
        if (userData.user?.profile) {
          setSettingsData({
            address: userData.user.profile.address || '',
            phone: userData.user.profile.phone || '',
            company: userData.user.profile.company || '',
            website: userData.user.profile.website || ''
          });
        }
      }
    } catch (error) {
      console.error('Fetch user data error:', error);
    }
  };

  // Handle settings update
  const handleUpdateSettings = async (settingsData) => {
    setIsSubmitting(true);
    try {
      console.log('Settings update started with data:', settingsData);
      
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }
      
      const response = await authenticatedFetch('/api/dashboard/user/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settingsData)
      });

      console.log('API response status:', response.status);
      console.log('API response ok:', response.ok);

      if (response.ok) {
        const result = await response.json();
        console.log('API response data:', result);
        console.log('Updated user profile from API:', result.user?.profile);
        showToast('Settings updated successfully!', 'success');
        
        // Update parent component's user state with the new profile data
        if (onUserChange && result.user) {
          const updatedUser = {
            ...user,
            profile: result.user.profile
          };
          onUserChange(updatedUser);
          
          // Update localStorage with new user data
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
        
        // Update settings data with the new values
        setSettingsData({
          address: result.user?.profile?.address || '',
          phone: result.user?.profile?.phone || '',
          company: result.user?.profile?.company || '',
          website: result.user?.profile?.website || '',
          timezone: result.user?.profile?.timezone || 'Europe/Istanbul'
        });
        
        setShowSettingsForm(false);
      } else {
        const errorData = await response.json();
        console.error('API error response:', errorData);
        showToast(errorData.message || 'Failed to update settings.', 'error');
      }
    } catch (error) {
      console.error('Update settings error:', error);
      showToast('An error occurred while updating settings.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle subscription management
  const handleManageSubscription = async () => {
    setIsLoadingPortal(true);
    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }

      // First, get or create Stripe customer
      let customerId = user?.subscription?.stripeCustomerId;
      
      if (!customerId) {
        // Create customer if doesn't exist
        const customerResponse = await authenticatedFetch('/api/payment/stripe/customer', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: user.email,
            name: `${user.firstName} ${user.lastName}`,
            userId: user.id || user._id
          })
        });

        if (customerResponse.ok) {
          const customerData = await customerResponse.json();
          customerId = customerData.customer.id;
        } else {
          throw new Error('Failed to create customer');
        }
      }

      // Create portal session
      const portalResponse = await authenticatedFetch('/api/payment/stripe/portal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId: customerId,
          returnUrl: `${window.location.origin}/dashboard/settings`
        })
      });

      if (portalResponse.ok) {
        const portalData = await portalResponse.json();
        window.location.href = portalData.url;
      } else {
        throw new Error('Failed to create portal session');
      }
    } catch (error) {
      console.error('Subscription management error:', error);
      showToast('Failed to open subscription management. Please try again.', 'error');
    } finally {
      setIsLoadingPortal(false);
    }
  };

  return (
    <div>
      <div className="bg-black rounded-2xl border border-gray-800 p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-medium text-white">Profile Information</h3>
          <Button
            onClick={() => setShowSettingsForm(true)}
            className="px-6 py-3 rounded-xl flex items-center transition-colors duration-200 data-hover:bg-gray-700 data-focus:ring-white border bg-gray-800 text-white hover:bg-gray-700 border-gray-700"
          >
            <FaEdit className="mr-2" />
            Edit Profile
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Full Name</label>
              <div className="bg-gray-800 rounded-lg p-4 text-white border border-gray-700">
                {user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'Not provided'}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
              <div className="bg-gray-800 rounded-lg p-4 text-white border border-gray-700">
                {user?.email || 'Not provided'}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
              <div className="bg-gray-800 rounded-lg p-4 text-white border border-gray-700">
                {user?.profile?.phone || 'Not provided'}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Company</label>
              <div className="bg-gray-800 rounded-lg p-4 text-white border border-gray-700">
                {user?.profile?.company || 'Not provided'}
              </div>
            </div>
          </div>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Address</label>
              <div className="bg-gray-800 rounded-lg p-4 text-white border border-gray-700">
                {user?.profile?.address || 'Not provided'}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Website</label>
              <div className="bg-gray-800 rounded-lg p-4 text-white border border-gray-700">
                {user?.profile?.website ? (
                  <a 
                    href={user.profile.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-indigo-400 hover:text-indigo-300 transition-colors"
                  >
                    {user.profile.website}
                  </a>
                ) : 'Not provided'}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Timezone</label>
              <div className="bg-gray-800 rounded-lg p-4 text-white border border-gray-700">
                {user?.profile?.timezone || 'Europe/Istanbul'}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Subscription Plan</label>
              <div className="bg-gray-800 rounded-lg p-4 text-white border border-gray-700">
                <div className="flex items-center justify-between">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    userPlan === 'enterprise' 
                      ? 'bg-purple-900 text-purple-300' 
                      : userPlan === 'premium'
                      ? 'bg-blue-900 text-blue-300'
                      : 'bg-gray-700 text-gray-300'
                  }`}>
                    {userPlan === 'enterprise' && <FaRocket className="mr-1" />}
                    {userPlan === 'premium' && <FaCrown className="mr-1" />}
                    {userPlan === 'free' && <FaStar className="mr-1" />}
                    {userPlan.charAt(0).toUpperCase() + userPlan.slice(1)} Plan
                  </span>
                  
                  {(userPlan === 'premium' || userPlan === 'enterprise') && (
                    <button
                      onClick={handleManageSubscription}
                      disabled={isLoadingPortal}
                      className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-sm"
                    >
                      {isLoadingPortal ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                          Loading...
                        </>
                      ) : (
                        <>
                          <FaCog className="mr-1" />
                          Manage
                        </>
                      )}
                    </button>
                  )}
                </div>
                
                {(user?.subscription?.plan === 'premium' || user?.subscription?.plan === 'enterprise') && (
                  <div className="mt-3 text-sm text-gray-400">
                    <p>Manage your subscription, billing, and payment methods</p>
                    {user?.subscription?.expiresAt && (
                      <p className="mt-1">
                        Expires: {new Date(user.subscription.expiresAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {(!user?.profile?.address || !user?.profile?.phone) && (
          <div className="mt-6 p-4 bg-yellow-900 border border-yellow-700 rounded-lg">
            <div className="flex items-center">
              <FaExclamationCircle className="h-5 w-5 text-yellow-400 mr-2" />
              <p className="text-yellow-200 text-sm">
                Complete your profile information to get the most out of your experience.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Logout Section */}
      <div className="bg-black rounded-2xl border border-gray-800 p-6 mt-6">
        <div className="flex items-center justify-between">
          <div className="text-left">
            <h3 className="text-lg font-medium text-white mb-2">Account Actions</h3>
            <p className="text-gray-400 text-sm">Manage your account and session</p>
          </div>
          <button
            onClick={() => onShowLogoutDialog && onShowLogoutDialog(true)}
            className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors flex items-center"
          >
            <FaSignOutAlt className="mr-2" />
            Log Out
          </button>
        </div>
      </div>

      {/* Settings Form Modal */}
      {showSettingsForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-semibold text-white">Edit Profile</h3>
                <p className="text-gray-400 text-sm mt-1">Update your profile information</p>
              </div>
              <button
                onClick={() => setShowSettingsForm(false)}
                className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-800 rounded-lg"
              >
                <FaTimes className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();
              handleUpdateSettings(settingsData);
            }} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Address *</label>
                  <input
                    type="text"
                    value={settingsData.address}
                    onChange={(e) => {
                      console.log('Address changed to:', e.target.value);
                      setSettingsData(prev => ({ ...prev, address: e.target.value }));
                    }}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-indigo-500 transition-colors"
                    placeholder="Enter your address"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Phone *</label>
                  <input
                    type="tel"
                    value={settingsData.phone}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Sadece rakam ve + karakterine izin ver
                      if (/^[\d+]*$/.test(value)) {
                        setSettingsData(prev => ({ ...prev, phone: value }));
                      }
                    }}
                    onKeyPress={(e) => {
                      // Sadece rakam ve + karakterine izin ver
                      if (!/[\d+]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-indigo-500 transition-colors"
                    placeholder="Enter your phone number (e.g., +1234567890)"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Company</label>
                  <input
                    type="text"
                    value={settingsData.company}
                    onChange={(e) => setSettingsData(prev => ({ ...prev, company: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-indigo-500 transition-colors"
                    placeholder="Enter your company name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Website</label>
                  <input
                    type="url"
                    value={settingsData.website}
                    onChange={(e) => setSettingsData(prev => ({ ...prev, website: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-indigo-500 transition-colors"
                    placeholder="Enter your website URL"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Timezone</label>
                  <select
                    value={settingsData.timezone}
                    onChange={(e) => setSettingsData(prev => ({ ...prev, timezone: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-indigo-500 transition-colors"
                  >
                    {timezones.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowSettingsForm(false)}
                  className="px-6 py-3 bg-gray-700 text-white rounded-xl hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-8 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Updating...
                    </>
                  ) : (
                    'Update Profile'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg transition-all duration-300 ${
          toast.type === 'success' 
            ? 'bg-green-900 text-green-200 border border-green-700' 
            : 'bg-red-900 text-red-200 border border-red-700'
        }`}>
          <div className="flex items-center">
            <div className="flex-1">
              <p className="font-medium">{toast.message}</p>
            </div>
            <button
              onClick={() => setToast({ show: false, message: '', type: 'success' })}
              className="ml-4 text-gray-400 hover:text-white"
            >
              <FaTimes className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
